// Compact live step list shown above the dashboard's tailoring box while a run
// is going, in the JobGPT style. Every line is a real engine stage reported by
// the tailoring page (lib/tailoringQuickStart); nothing here is timed or faked.
import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import {
  QUICK_START_STEPS, getQuickStartProgress, subscribeQuickStartProgress, requestQuickStartStop, type QuickStartProgress,
} from "@/lib/tailoringQuickStart";
import { getTailoringRunStatus, subscribeTailoringRunStatus } from "@/lib/tailoringRunStatus";

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export function TailoringSteps({ title }: { title: string }) {
  const [p, setP] = useState<QuickStartProgress>(getQuickStartProgress());
  const [now, setNow] = useState(Date.now());
  useEffect(() => subscribeQuickStartProgress(setP), []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const steps = QUICK_START_STEPS.filter((s) => !s.deepOnly || p.deep);
  // The engine's own timer is the source of truth (it keeps counting across a
  // tab reload or a late-mounted panel); the local start time is the backup.
  const [runStatus, setRunStatus] = useState(getTailoringRunStatus());
  useEffect(() => subscribeTailoringRunStatus(setRunStatus), []);
  const engineElapsed = runStatus?.active ? Number(runStatus.elapsedSeconds ?? 0) : 0;
  const localElapsed = p.startedAt ? Math.max(0, Math.round((now - p.startedAt) / 1000)) : 0;
  const elapsed = Math.max(engineElapsed, localElapsed);

  // "Step X of Y": X is the active step's position, or the count of
  // finished steps once everything is done (never below 1, never above Y).
  const doneCount = steps.filter((s) => p.steps[s.id] === "done").length;
  const runningCount = steps.filter((s) => p.steps[s.id] === "active").length;
  const secs = (id: string) => {
    const b = (p.began as any)[id] as number | undefined; if (!b) return null;
    const e = ((p.ended as any)[id] as number | undefined) ?? now;
    return Math.max(0, Math.round((e - b) / 1000));
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 mb-3 text-left shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-[13px] font-bold text-[#000F36] truncate">{title}</span>
        <span className="flex items-center gap-2 shrink-0">
          <span className="text-[11.5px] font-semibold text-slate-400 tabular-nums">{doneCount} of {steps.length} done{runningCount > 1 ? ` · ${runningCount} running at once` : ""}</span>
          <span className="text-[12px] font-semibold tabular-nums text-slate-500">{fmt(elapsed)}</span>
          <button
            type="button"
            onClick={() => requestQuickStartStop()}
            className="ml-1 inline-flex items-center rounded-md border border-red-300 px-2.5 py-1 text-[11.5px] font-semibold text-red-600 hover:bg-red-50"
            title="Stop this tailoring run. Nothing is saved and no credits are used."
          >
            Stop
          </button>
        </span>
      </div>
      <ul className="space-y-1.5">
        {steps.map((s) => {
          const status = p.steps[s.id] ?? "pending";
          return (
            <li key={s.id} className="flex items-center gap-2 text-[13px] leading-snug">
              {status === "done" ? (
                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
              ) : status === "active" ? (
                <Loader2 className="w-4 h-4 shrink-0 text-[#CD9700] animate-spin" />
              ) : (
                <Circle className="w-4 h-4 shrink-0 text-slate-300" />
              )}
              <span className={
                status === "done" ? "text-slate-500" :
                status === "active" ? "font-semibold text-[#000F36]" : "text-slate-400"
              }>
                {status === "done" ? s.done : s.active}
              </span>
              {secs(s.id) !== null && (
                <span className={`ml-auto pl-2 text-[11.5px] tabular-nums ${status === "active" ? "font-semibold text-[#CD9700]" : "text-slate-400"}`}>
                  {fmt(secs(s.id)!)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[11.5px] text-slate-400">
        You can leave this page. Your results open when it's done.
      </p>
    </div>
  );
}
